import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Grid, Link, Snackbar, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import {
  GET_USER_BILLING,
  GET_SUBSCRIPTION_PLANS,
  GET_USAGE_STATS,
  GET_BILLING_INVOICES,
  INITIALIZE_USER_BILLING,
} from '../../graphql/billingQueries';
import { useActivePaymentProvider } from '../../hooks/useActivePaymentProvider';
import LoadingState from '../ui/LoadingState';
import PageContainer from '../ui/PageContainer';
import PageHeader from '../ui/PageHeader';
import { CancelSubscriptionDialog, ConfirmDialog, PlanChangeConfirmDialog, SwitchToFreeDialog } from './BillingDialogs';
import { FREE_PLAN_ID, formatLimit, formatSizeLimit, isFreePlan, sortPlansByPrice, summarizeBilling } from './billingModel';
import type { BillingInfo, Invoice, SubscriptionPlan, UsageStats } from './billingTypes';
import CurrentPlanSection, { type PlanAction } from './CurrentPlanSection';
import InvoicesSection from './InvoicesSection';
import { MonerisTokenization } from './MonerisTokenization';
import PaymentMethodSection, { usePaymentPortal } from './PaymentMethodSection';
import PlanHistoryDialog from './PlanHistoryDialog';
import PlanPickerDialog from './PlanPickerDialog';
import UsageSection from './UsageSection';
import { useBillingActions } from './useBillingActions';

// Kept for existing importers of this module.
export type { BillingInfo, UsageStats, SubscriptionPlan, UsageBreakdown, UsageWarning } from './billingTypes';
export { FREE_PLAN_ID, isFreePlan } from './billingModel';

const PAGE_TITLE = 'Plan and billing';
const PAGE_SUBTITLE = 'Your plan, usage, payment method and invoices.';

/** Navigation state that opens the plan picker on arrival (from the payment result pages). */
export interface BillingLocationState {
  openPlanPicker?: boolean;
}

/**
 * /billing. Reads the billing record once (billingModel.summarizeBilling) and
 * hands the same answer to every section, so the plan panel, the picker, the
 * payment method and the dialogs can't disagree about whether the account
 * pays, what it pays and what it can do.
 */
const BillingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showHistory, setShowHistory] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const billingRepairAttempted = useRef(false);

  const {
    data: billingData,
    loading: billingLoading,
    error: billingError,
    refetch: refetchBilling,
  } = useQuery(GET_USER_BILLING, { skip: !user, errorPolicy: 'none' });
  const { data: plansData, loading: plansLoading, error: plansError, refetch: refetchPlans } = useQuery(GET_SUBSCRIPTION_PLANS);
  const {
    data: usageData,
    loading: usageLoading,
    error: usageError,
    refetch: refetchUsage,
  } = useQuery(GET_USAGE_STATS, { skip: !user, errorPolicy: 'none' });
  const {
    data: invoicesData,
    loading: invoicesLoading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery(GET_BILLING_INVOICES, { variables: { limit: 25 }, skip: !user });
  const { provider, loading: providerLoading, refetch: refetchProvider } = useActivePaymentProvider();
  const [initializeBilling] = useMutation(INITIALIZE_USER_BILLING);

  const billing: BillingInfo | undefined = billingData?.getUserBilling ?? undefined;
  const plans = useMemo<SubscriptionPlan[]>(() => sortPlansByPrice(plansData?.getSubscriptionPlans || []), [plansData]);
  const summary = useMemo(() => summarizeBilling(billing, plans), [billing, plans]);
  const usage: UsageStats | undefined = usageData?.getUsageStats ?? undefined;
  const invoices: Invoice[] = invoicesData?.getUserInvoices || [];

  // A first visit can arrive before the account's billing record exists.
  useEffect(() => {
    if (!user || billingLoading || billingError || billingData?.getUserBilling || billingRepairAttempted.current) return;
    billingRepairAttempted.current = true;
    setCreatingUser(true);
    initializeBilling({ variables: { userId: user.uid, planId: 'basic' } })
      .then(async () => {
        await Promise.all([refetchBilling(), refetchUsage()]);
      })
      .catch((error) => console.error('Failed to repair billing profile:', error))
      .finally(() => setCreatingUser(false));
  }, [billingData, billingError, billingLoading, initializeBilling, refetchBilling, refetchUsage, user]);

  const actions = useBillingActions({
    user,
    billing,
    plans,
    summary,
    refetchBilling,
    refetchUsage,
    refetchInvoices,
    activePaymentProvider: provider,
    providerLoading,
  });
  const portal = usePaymentPortal({ provider, billing, returnPath: '/billing' });

  // "Choose a plan" on the payment result pages lands here with the picker open.
  const pickerRequested = Boolean((location.state as BillingLocationState | null)?.openPlanPicker);
  const { openPlanPicker } = actions;
  const hasBilling = Boolean(billing);
  const phase = summary.phase;
  useEffect(() => {
    if (!pickerRequested || !hasBilling) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // A pending cancellation has to be undone ("Keep Pro") before any change.
    if (phase !== 'ending') openPlanPicker();
  }, [hasBilling, location.pathname, location.search, navigate, openPlanPicker, phase, pickerRequested]);

  const retryInitialization = async () => {
    if (!user) return;
    setCreatingUser(true);
    try {
      await initializeBilling({ variables: { userId: user.uid, planId: 'basic' } });
      await Promise.all([refetchBilling(), refetchUsage()]);
    } catch (error) {
      console.error('Failed to initialize billing:', error);
      actions.notify("We couldn't set up billing. Please try again, or contact support if this keeps happening.", 'error');
    } finally {
      setCreatingUser(false);
    }
  };

  // Errors and warnings appear in the dialog the customer is looking at, or
  // on the page when none is open; successes are a short snackbar.
  const notice = actions.notice;
  const problemNotice = notice && (notice.severity === 'error' || notice.severity === 'warning') ? notice : null;
  const toastNotice = notice && !problemNotice ? notice : null;
  const topDialog = actions.confirmRequest
    ? 'confirm'
    : actions.planChangeConfirm
      ? 'planChange'
      : actions.switchToFreeOpen
        ? 'switchToFree'
        : actions.showCancelDialog
          ? 'cancel'
          : actions.showPlanChange
            ? 'picker'
            : null;
  const errorFor = (dialog: typeof topDialog) => (topDialog === dialog ? problemNotice : null);

  const toast = (
    <Snackbar
      open={Boolean(toastNotice)}
      autoHideDuration={6000}
      onClose={(_, reason) => {
        if (reason !== 'clickaway') actions.clearNotice();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={toastNotice?.severity || 'success'}
        onClose={actions.clearNotice}
        sx={{ whiteSpace: 'pre-line', maxWidth: 560, boxShadow: 6 }}
      >
        {toastNotice?.message}
      </Alert>
    </Snackbar>
  );

  const header = <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />;

  // Until the first repair attempt has run, a missing record means "setting up", not "failed".
  const awaitingRepair = !billing && !billingError && !billingRepairAttempted.current;
  if (!user || (billingLoading && !billing) || (plansLoading && !plansData) || creatingUser || awaitingRepair) {
    return (
      <PageContainer>
        {header}
        <LoadingState variant="list" count={3} label="Loading plan and billing" />
        {toast}
      </PageContainer>
    );
  }

  if (billingError && !billing) {
    return (
      <PageContainer>
        {header}
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void refetchBilling();
                void refetchUsage();
              }}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your plan and billing. Check your connection and try again.
        </Alert>
        {toast}
      </PageContainer>
    );
  }

  if (!billing) {
    return (
      <PageContainer>
        {header}
        {problemNotice && (
          <Alert severity={problemNotice.severity} onClose={actions.clearNotice} sx={{ mb: 2 }}>
            {problemNotice.message}
          </Alert>
        )}
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => void retryInitialization()} disabled={creatingUser}>
              Try again
            </Button>
          }
        >
          We couldn&apos;t set up billing for this account. Your profile and files aren&apos;t affected.
        </Alert>
        {toast}
      </PageContainer>
    );
  }

  const freePlan = plans.find((plan) => plan.id === FREE_PLAN_ID) || plans.find((plan) => isFreePlan(plan));
  const freePlanName = freePlan?.name || 'Free';
  const freeLimitsLabel = freePlan
    ? `${formatSizeLimit(freePlan.limits.storage)}, ${formatLimit(freePlan.limits.projects)} active projects`
    : '1 GB, 3 active projects';

  // One primary action for the plan panel.
  let primaryAction: PlanAction | null;
  let footnote: string | null = null;
  if (summary.phase === 'ending') {
    primaryAction = {
      label: actions.keepPlanLabel,
      onClick: () => void actions.handleKeepPlan(),
      busy: actions.keepBusy,
      busyLabel: 'Working…',
      disabled: providerLoading,
    };
    if (!summary.planIsFree) footnote = `To switch to a different plan, keep ${summary.planName} first.`;
  } else if (summary.phase === 'payment_issue' && portal.available) {
    primaryAction = { label: 'Update card', onClick: () => void portal.open(), busy: portal.opening, busyLabel: 'Opening…' };
  } else {
    primaryAction = {
      label: summary.planIsFree ? 'Upgrade' : 'Change plan',
      onClick: openPlanPicker,
      disabled: plans.length === 0,
    };
  }

  const showInvoices = summary.hasLiveSubscription || invoices.length > 0;

  return (
    <PageContainer>
      {header}

      {topDialog === null && problemNotice && (
        <Alert severity={problemNotice.severity} onClose={actions.clearNotice} sx={{ mb: 3, whiteSpace: 'pre-line' }}>
          {problemNotice.message}
        </Alert>
      )}

      {plansError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetchPlans()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load the plan list, so some plan details are missing.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <CurrentPlanSection
            summary={summary}
            primaryAction={primaryAction}
            onCancel={actions.openCancelDialog}
            cancelDisabled={providerLoading}
            onShowHistory={() => setShowHistory(true)}
            footnote={footnote}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PaymentMethodSection
            id="payment-method"
            billing={billing}
            hasLiveSubscription={summary.hasLiveSubscription}
            provider={provider}
            providerLoading={providerLoading}
            onRetryProvider={refetchProvider}
            portal={portal}
            onSeePlans={openPlanPicker}
          />
        </Grid>
        <Grid item xs={12}>
          <UsageSection
            usage={usage}
            loading={usageLoading}
            error={Boolean(usageError)}
            onRetry={() => void refetchUsage()}
            plan={summary.plan}
            plans={plans}
            planId={summary.planId}
          />
        </Grid>
        {showInvoices && (
          <Grid item xs={12}>
            <InvoicesSection
              invoices={invoices}
              loading={invoicesLoading}
              error={Boolean(invoicesError)}
              onRetry={() => void refetchInvoices()}
            />
          </Grid>
        )}
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Questions about a charge? Email{' '}
        <Link href="mailto:info@artbits.solutions?subject=Billing%20question">info@artbits.solutions</Link>.
      </Typography>

      <PlanPickerDialog
        open={actions.showPlanChange}
        plans={plans}
        summary={summary}
        selectedPlanId={actions.selectedPlan}
        onSelect={actions.selectPlan}
        onClose={actions.closePlanPicker}
        onContinue={() => void actions.handlePlanChange()}
        continueLabel={actions.continueLabel}
        busy={actions.planChangeBusy}
        waiting={providerLoading}
        error={errorFor('picker')}
        onDismissError={actions.clearNotice}
      />

      <PlanChangeConfirmDialog
        pending={actions.planChangeConfirm}
        summary={summary}
        busy={actions.confirmBusy}
        onCancel={actions.cancelPlanChangeConfirm}
        onConfirm={() => void actions.confirmStripePlanChange()}
        error={errorFor('planChange')}
        onDismissError={actions.clearNotice}
      />

      <SwitchToFreeDialog
        open={actions.switchToFreeOpen}
        summary={summary}
        busy={actions.switchBusy}
        onKeep={actions.closeSwitchToFree}
        onConfirm={() => void actions.confirmSwitchToFree()}
        freePlanName={freePlanName}
        freeLimitsLabel={freeLimitsLabel}
        error={errorFor('switchToFree')}
        onDismissError={actions.clearNotice}
      />

      <CancelSubscriptionDialog
        open={actions.showCancelDialog}
        summary={summary}
        busy={actions.cancelBusy}
        disabled={providerLoading}
        onKeep={actions.closeCancelDialog}
        onConfirm={() => void actions.handleCancelSubscription()}
        freePlanName={freePlanName}
        freeLimitsLabel={freeLimitsLabel}
        error={errorFor('cancel')}
        onDismissError={actions.clearNotice}
      />

      <ConfirmDialog
        request={actions.confirmRequest}
        onSettle={actions.settleConfirm}
        error={errorFor('confirm')}
        onDismissError={actions.clearNotice}
      />

      <PlanHistoryDialog open={showHistory} onClose={() => setShowHistory(false)} />

      {/* Card entry for Moneris; Stripe (the default) uses its hosted checkout instead. */}
      {provider === 'moneris' && (
        <MonerisTokenization
          key={actions.tokenizationTicket}
          open={actions.showTokenizationDialog}
          tokenUrl={actions.tokenizationUrl}
          ticketId={actions.tokenizationTicket}
          onComplete={actions.handleTokenizationComplete}
          onCancel={() => void actions.handleTokenizationCancel()}
        />
      )}

      {toast}
    </PageContainer>
  );
};

export default BillingDashboard;
